import React, { useState, useEffect } from 'react'
import CelebrationBurst from '../animations/CelebrationBurst'
import './ReportGameWrapper.css'

/**
 * Wrapper component for report games
 * Provides consistent UI, progress tracking, and completion handling
 */
export default function ReportGameWrapper({
  children,
  title,
  description,
  progress = 0,
  showProgress = true,
  onComplete,
  theme = {},
  celebrateMilestone = null,
  className = ''
}) {
  const [showCelebration, setShowCelebration] = useState(false)
  const [isComplete, setIsComplete] = useState(false)

  // Default theme values
  const defaultTheme = {
    primaryColor: '#3b82f6', // Blue-500
    secondaryColor: '#10b981', // Green-500
    backgroundColor: '#ffffff',
    borderColor: '#e5e7eb',
    textColor: '#1f2937',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  }

  const mergedTheme = { ...defaultTheme, ...theme }

  // Handle completion
  useEffect(() => {
    if (progress >= 100 && !isComplete) {
      setIsComplete(true)
      if (onComplete) {
        onComplete()
      }
      // Show celebration for completion
      setShowCelebration(true)
    }
  }, [progress, isComplete, onComplete])

  // Handle milestone celebrations
  useEffect(() => {
    if (celebrateMilestone && !showCelebration) {
      setShowCelebration(true)
    }
  }, [celebrateMilestone, showCelebration])

  return (
    <div
      className={`report-game-wrapper ${className}`}
      style={{
        '--game-primary': mergedTheme.primaryColor,
        '--game-secondary': mergedTheme.secondaryColor,
        '--game-bg': mergedTheme.backgroundColor,
        '--game-border': mergedTheme.borderColor,
        '--game-text': mergedTheme.textColor,
        fontFamily: mergedTheme.fontFamily,
        backgroundColor: mergedTheme.backgroundColor,
        border: `1px solid ${mergedTheme.borderColor}`,
        borderRadius: '12px',
        padding: '24px',
        position: 'relative',
        marginTop: '48px',
        marginBottom: '24px'
      }}
    >
      {/* Header */}
      <div className="game-header" style={{ marginBottom: '20px' }}>
        {title && (
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: mergedTheme.textColor,
            marginBottom: '8px'
          }}>
            {title}
          </h3>
        )}
        {description && (
          <p style={{
            fontSize: '0.95rem',
            color: mergedTheme.textColor,
            opacity: 0.7,
            marginBottom: '16px'
          }}>
            {description}
          </p>
        )}
      </div>

      {/* Progress Bar */}
      {showProgress && (
        <div className="game-progress" style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '8px',
            fontSize: '0.875rem',
            color: mergedTheme.textColor,
            opacity: 0.6
          }}>
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div style={{
            width: '100%',
            height: '8px',
            backgroundColor: mergedTheme.borderColor,
            borderRadius: '4px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${mergedTheme.primaryColor}, ${mergedTheme.secondaryColor})`,
                borderRadius: '4px',
                transition: 'width 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Animated shimmer effect */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                animation: progress > 0 && progress < 100 ? 'shimmer 2s infinite' : 'none'
              }} />
            </div>
          </div>
        </div>
      )}

      {/* Game Content */}
      <div className="game-content">
        {children}
      </div>

      {/* Completion Modal */}
      {isComplete && (
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          borderRadius: '12px',
          zIndex: 10
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            textAlign: 'center',
            maxWidth: '400px',
            animation: 'bounce-in 0.5s ease-out'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🎉</div>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: mergedTheme.textColor,
              marginBottom: '8px'
            }}>
              Game Complete!
            </h3>
            <p style={{
              color: mergedTheme.textColor,
              opacity: 0.7
            }}>
              Great job! You've completed this activity.
            </p>
          </div>
        </div>
      )}

      {/* Celebration Animation */}
      <CelebrationBurst
        show={showCelebration}
        milestone={celebrateMilestone || 'complete'}
        onComplete={() => setShowCelebration(false)}
      />

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }

        @keyframes bounce-in {
          0% {
            transform: scale(0.95);
            opacity: 0;
          }
          70% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  )
}